import type { ReactNode } from 'react';

/**
 * Wash-filled chip (Direction-B hybrid redesign) — a soft-wash background +
 * optional leading dot, keyed to the semantic tokens.
 *
 * 2026-08-19 remediation Slice D, Task 3: reserved for FLOATING CHROME only
 * (badges, the session drawer's live status) — DESIGN.md's rule for status
 * IN A DATA ROW is a leading colour dot + label text, never a wash-filled
 * chip (also never colour alone — the WCAG 1.4.1 requirement). That idiom is
 * `StatusLabel` (`components/ui/StatusLabel.tsx`); use it, not `Pill`, for
 * any status column in a table or list.
 */
export type PillVariant = 'ok' | 'warn' | 'bad' | 'mute' | 'neutral' | 'accent';

const PILL: Record<PillVariant, string> = {
  ok: 'bg-ok-wash text-ok',
  warn: 'bg-warn-wash text-warn',
  bad: 'bg-bad-wash text-bad',
  mute: 'bg-mute-wash text-mute ring-1 ring-inset ring-rule',
  neutral: 'bg-wash text-ink-2 ring-1 ring-inset ring-rule',
  accent: 'bg-teal-wash text-teal',
};

const DOT: Record<PillVariant, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  mute: 'bg-mute',
  neutral: 'bg-ink-3',
  accent: 'bg-teal',
};

export function Pill({
  variant,
  children,
  dot = true,
  className = '',
}: {
  variant: PillVariant;
  children: ReactNode;
  /** Show the leading status dot (default true). */
  dot?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[12.5px] font-medium ${PILL[variant]} ${className}`}
    >
      {dot && <span className={`size-1.5 shrink-0 rounded-full ${DOT[variant]}`} aria-hidden />}
      {children}
    </span>
  );
}
